import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Icon } from "../../ui";
import {
  metaAdsLivePreviewDraftReady,
  getMetaAdPreviewEmbedDimensions,
  rewriteMetaPreviewIframeHtmlDimensions,
} from "./metaAdsUtils";
import { useMetaAdsAdPreview } from "../../../hooks/metaAds/useMetaAdsAdPreview";
import { useMetaAdsAdLivePreview } from "../../../hooks/metaAds/useMetaAdsAdPreviewLive";

/**
 * Decode minimal HTML entities in attribute-extracted URLs (fallback path).
 */
function decodeHtmlEntitiesInUrl(url) {
  if (!url || typeof url !== "string") {
    return url;
  }
  return url
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

function parseIframeTagDimensions(raw) {
  const widthMatch = /\bwidth=["']?(\d+)["']?/i.exec(raw);
  const heightMatch = /\bheight=["']?(\d+)["']?/i.exec(raw);
  return {
    width: widthMatch?.[1] ? Number(widthMatch[1]) : 335,
    height: heightMatch?.[1] ? Number(heightMatch[1]) : 450,
  };
}

/**
 * Derive iframe embed from Meta preview `body`.
 * Prefer wrapping the raw `<iframe …>` in `srcDoc` so the browser decodes `&amp;` etc. in `src`
 * (regex-extracting `src` leaves entities broken and breaks `preview_iframe.php` URLs).
 */
function metaPreviewIframeFromBody(body, adFormat) {
  const rawIn = String(body ?? "").trim();
  if (!rawIn) {
    return null;
  }
  const raw = /^<iframe\b/i.test(rawIn)
    ? rewriteMetaPreviewIframeHtmlDimensions(rawIn, adFormat)
    : rawIn;

  if (/^<iframe\b/i.test(raw)) {
    const dims = parseIframeTagDimensions(raw);
    return {
      kind: "wrappedIframeDoc",
      previewWidth: dims.width,
      previewHeight: dims.height,
      srcDoc: `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><style>html,body{margin:0;padding:0;background:#e9ebee;height:auto;min-height:0}body{display:flex;justify-content:center;align-items:flex-start;padding:8px;box-sizing:border-box}iframe{display:block;border:0;vertical-align:top}</style></head><body>${raw}</body></html>`,
    };
  }

  const iframeOpen = raw.match(/^<iframe\b[^>]*>/i);
  if (iframeOpen) {
    const tag = iframeOpen[0];
    const srcMatch = /\bsrc=["']([^"']+)["']/i.exec(tag);
    const widthMatch = /\bwidth=["']?(\d+)["']?/i.exec(tag);
    const heightMatch = /\bheight=["']?(\d+)["']?/i.exec(tag);
    if (srcMatch?.[1]) {
      return {
        kind: "src",
        src: decodeHtmlEntitiesInUrl(srcMatch[1]),
        width: widthMatch?.[1] ? Number(widthMatch[1]) : undefined,
        height: heightMatch?.[1] ? Number(heightMatch[1]) : undefined,
      };
    }
  }

  if (/^<!DOCTYPE|^<html[\s>]/i.test(raw)) {
    return { kind: "srcDoc", srcDoc: raw };
  }

  return {
    kind: "srcDoc",
    srcDoc: `<!DOCTYPE html><html><head><meta charset="utf-8"/></head><body>${raw}</body></html>`,
  };
}

/**
 * Embeds Meta Graph ad preview HTML from the API (`ad_format` from Marketing API).
 * Provider scripts can render inside the preview, but never inherit the host
 * origin. Live SDK compatibility remains a separate integration check.
 */
const MetaAdsGraphAdPreview = ({
  adId,
  className,
  graphLiveDraft,
  adFormat = "MOBILE_FEED_STANDARD",
  fillHeight = false,
  fetchEnabled = true,
}) => {
  const { t } = useTranslation("metaAds");
  const normalizedId = String(adId ?? "").trim();
  const fmt =
    String(adFormat || "MOBILE_FEED_STANDARD")
      .trim()
      .toUpperCase() || "MOBILE_FEED_STANDARD";

  const liveFormReady = metaAdsLivePreviewDraftReady(graphLiveDraft);

  const savedQuery = useMetaAdsAdPreview(normalizedId, {
    adFormat: fmt,
    enabled: Boolean(
      fetchEnabled && normalizedId && (!graphLiveDraft || !liveFormReady),
    ),
  });

  const liveQuery = useMetaAdsAdLivePreview(normalizedId, graphLiveDraft, {
    adFormat: fmt,
    enabled: Boolean(
      fetchEnabled && normalizedId && graphLiveDraft && liveFormReady,
    ),
  });

  const usingLive = Boolean(graphLiveDraft && liveFormReady);
  const data = usingLive ? liveQuery.data : savedQuery.data;
  const isLoading = usingLive ? liveQuery.isLoading : savedQuery.isLoading;
  const isError = usingLive ? liveQuery.isError : savedQuery.isError;
  const error = usingLive ? liveQuery.error : savedQuery.error;
  const refetch = usingLive ? liveQuery.refetch : savedQuery.refetch;
  const isFetching = usingLive ? liveQuery.isFetching : savedQuery.isFetching;

  const iframeConfig = useMemo(
    () => metaPreviewIframeFromBody(data?.body, fmt),
    [data?.body, fmt],
  );

  const embedDims = useMemo(() => getMetaAdPreviewEmbedDimensions(fmt), [fmt]);
  const frameOuterMaxPx = embedDims.width + 32;
  const frameWrapStyle = { maxWidth: `min(100%, ${frameOuterMaxPx}px)` };

  const wrappedH = (iframeConfig?.previewHeight ?? embedDims.height) + 28;
  /** Cap prevents oversized blanks; Meta snippet height drives the frame. */
  const compactIframeHeight = Math.min(Math.max(wrappedH, 240), 820);

  return (
    <div
      className={cn(
        "ordering-provider-preview border-border bg-card overflow-hidden rounded-xl border",
        fillHeight &&
          "flex min-h-0 flex-1 flex-col rounded-none border-0 bg-transparent shadow-none",
        !fillHeight && "h-fit max-w-full shrink-0",
        className,
      )}
    >
      <div
        className={cn(
          "bg-[#0a0a0a] px-2 pt-2 pb-2 sm:px-3 sm:pt-3 sm:pb-2",
          fillHeight && "flex min-h-0 flex-1 flex-col",
          !fillHeight && "flex justify-center",
        )}
      >
        {!normalizedId ? (
          <p className="text-muted-foreground px-2 py-8 text-center text-sm">
            {t("create.previewGraphNoAdId")}
          </p>
        ) : isLoading ? (
          <div
            className={cn(
              "mx-auto flex w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-zinc-900/80",
              fillHeight ? "min-h-0 flex-1" : "min-h-[200px] py-8",
            )}
            style={frameWrapStyle}
            aria-busy="true"
            aria-label={t("create.previewGraphLoading")}
          >
            <div
              className="bg-muted/40 mx-auto h-44 animate-pulse rounded-lg"
              style={{ width: `min(100%, ${embedDims.width}px)` }}
            />
          </div>
        ) : isError ? (
          <div
            className={cn(
              "space-y-3 px-3 py-10 text-center",
              fillHeight && "flex flex-1 flex-col justify-center",
            )}
          >
            <p className="text-destructive text-sm">
              {error?.message || t("create.previewGraphError")}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="text-brand-cyan hover:text-brand-cyan/85 text-sm font-medium underline-offset-2 hover:underline disabled:opacity-50"
            >
              {t("create.previewGraphRetry")}
            </button>
          </div>
        ) : iframeConfig?.kind === "wrappedIframeDoc" ? (
          <div
            className={cn(
              "mx-auto w-full overflow-hidden rounded-lg bg-[#e9ebee] shadow-sm ring-1 ring-black/20",
              fillHeight
                ? "min-h-0 flex-1 overflow-y-auto overscroll-contain"
                : "shrink-0",
            )}
            style={frameWrapStyle}
          >
            <iframe
              sandbox="allow-scripts"
              title={t("create.previewGraphIframeTitle")}
              srcDoc={iframeConfig.srcDoc}
              className="block w-full border-0 bg-[#e9ebee]"
              style={{
                height: fillHeight
                  ? `${Math.max(wrappedH, embedDims.height + 24)}px`
                  : `${compactIframeHeight}px`,
              }}
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : iframeConfig?.kind === "src" ? (
          <div
            className={cn(
              "mx-auto flex w-full justify-center overflow-hidden rounded-lg bg-[#e9ebee] shadow-sm ring-1 ring-black/20",
              fillHeight
                ? "min-h-0 flex-1 overflow-y-auto overscroll-contain"
                : "shrink-0 overflow-x-auto",
            )}
            style={frameWrapStyle}
          >
            <iframe
              sandbox="allow-scripts"
              title={t("create.previewGraphIframeTitle")}
              src={iframeConfig.src}
              className="block border-0 bg-[#e9ebee]"
              style={{
                width: `${embedDims.width}px`,
                height: `${Math.min(
                  embedDims.height + 24,
                  fillHeight ? 1200 : compactIframeHeight,
                )}px`,
                maxHeight: fillHeight ? "none" : `${compactIframeHeight}px`,
              }}
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : iframeConfig?.kind === "srcDoc" ? (
          <div
            className={cn(
              "mx-auto w-full overflow-hidden rounded-lg bg-zinc-950 shadow-sm ring-1 ring-white/10",
              fillHeight
                ? "min-h-0 flex-1 overflow-y-auto overscroll-contain"
                : "shrink-0",
            )}
            style={frameWrapStyle}
          >
            <iframe
              sandbox="allow-scripts"
              title={t("create.previewGraphIframeTitle")}
              srcDoc={iframeConfig.srcDoc}
              className="block min-h-0 w-full border-0 bg-white"
              style={{
                height: fillHeight
                  ? "min(720px, 85vh)"
                  : `${Math.min(
                      embedDims.height + 48,
                      Math.max(compactIframeHeight, 280),
                    )}px`,
                maxHeight: fillHeight ? "min(85vh, 900px)" : undefined,
              }}
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : (
          <p
            className={cn(
              "text-muted-foreground px-2 py-8 text-center text-sm",
              fillHeight && "flex flex-1 flex-col justify-center",
            )}
          >
            {t("create.previewGraphEmptyBody")}
          </p>
        )}
      </div>

      <div className="border-border bg-muted/20 shrink-0 border-t border-white/10">
        <div className="flex flex-col items-stretch gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="text-muted-foreground text-center text-[10px] leading-snug sm:flex-1 sm:text-start">
            {t("create.previewGraphExpiryHint")}
          </p>
          {normalizedId ? (
            <div className="flex shrink-0 justify-center sm:justify-end">
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className={cn(
                  "text-brand-cyan hover:text-brand-cyan/85 border-brand-cyan/30 bg-brand-cyan/5 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[11px] font-medium transition",
                  "focus-visible:ring-brand-cyan/35 focus-visible:ring-2 focus-visible:outline-none",
                  "disabled:pointer-events-none disabled:opacity-50",
                )}
                aria-label={t("create.previewGraphRefreshPreviewAria")}
              >
                <Icon
                  name="RefreshCw"
                  className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
                  aria-hidden
                />
                {t("create.previewGraphRefreshPreview")}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MetaAdsGraphAdPreview;
